import { useState, useRef, useEffect } from "react";
import { Upload, ArrowLeft } from "lucide-react";
import localization from "services/localization";
import { Switch } from "components/ui/switch";
import { toast } from "react-toastify";
import { ClipLoader } from "react-spinners";

export default function ResourceUpload({
  onResourceUpload,
  onBackClick,
  isModal = false,
  appUser,
  userData,
  loading,
  resourceType,
  uploadError,
  setUploadError,
}) {
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [toRag, setToRag] = useState(false);
  const [visibilityScope, setVisibilityScope] = useState("public");

  const fileInputRef = useRef(null);
  const isVisibilityDisabled =
    appUser?.role === "teacher" || appUser?.role === "administrative";

  useEffect(() => {
    if (isVisibilityDisabled) setVisibilityScope("private");
  }, [isVisibilityDisabled]);

  useEffect(() => {
    if (resourceType === "rag") {
      setToRag(true);
    } else if (resourceType === "nonRag") {
      setToRag(false);
    }
  }, [resourceType]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length)
      setFiles(Array.from(e.dataTransfer.files));
  };

  const handleChange = (e) => {
    if (!e.target.files?.length) return;
    setFiles(Array.from(e.target.files));
    setUploadError("");
  };

  const handleUpload = async () => {
    if (!files.length || loading) return;

    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("files[]", file));
      files.forEach((file) => formData.append("file_size", file.size));
      formData.append("to_rag", String(toRag));
      formData.append("visibility_scope", visibilityScope);
      formData.append("school_id", userData?.selected_school_id);
      formData.append("user_id", appUser?.id);
      // formData.append("enabled", true);

      const res = await onResourceUpload(formData);

      if (res?.status === 200 || res?.status === 201) {
        setFiles([]);
        setToRag(true);
        isVisibilityDisabled
          ? setVisibilityScope("private")
          : setVisibilityScope("public");
        if (fileInputRef.current) fileInputRef.current.value = "";
        onBackClick();
      }
    } catch (error) {
      const msg = error?.response?.data?.error || "Failed to upload resources!";

      if (msg.includes("File already exists")) {
        setUploadError(
          localization.existing_file_message ||
            "A file with this name already exists. Please rename the file and upload again or use the existing file.",
        );
      } else {
        toast.error(msg);

        setFiles([]);
        setToRag(true);
        isVisibilityDisabled
          ? setVisibilityScope("private")
          : setVisibilityScope("public");
        if (fileInputRef.current) fileInputRef.current.value = "";
        onBackClick(); // Close modal
      }
    }
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith("image/")) return "🖼️";
    if (file.type === "application/pdf") return "📄";
    if (file.type.includes("spreadsheet")) return "📊";
    return "📋";
  };

  const isRagSwitchDisabled =
    resourceType === "rag" || resourceType === "nonRag";

  return (
    <div className="resource-upload">
      {loading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-50">
          <ClipLoader size={50} color="var(--primary-icon)" />
        </div>
      )}
      <div className="resource-upload-container">
        {!isModal && (
          <button onClick={onBackClick} className="resource-upload-back-btn">
            <ArrowLeft size={20} />
            {localization.back_to_library || "Back to Library"}
          </button>
        )}

        <div className="resource-upload-header">
          <h1 className="resource-upload-title">
            {localization.add_new_resource || "Add New Resource"}
          </h1>
          <p className="resource-upload-subtitle">
            {localization.upload_drag_file ||
              "Upload or drag and drop your file to add it to your library"}
          </p>
        </div>

        <div className="resource-upload-body">
          {/* Drag & Drop */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`drag-zone ${dragActive ? "drag-active" : ""}`}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleChange}
              className="drag-input"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
            />

            <div className="drag-content">
              <div
                className={`drag-icon ${dragActive ? "drag-active-icon" : ""}`}
              >
                <Upload size={48} />
              </div>
              <div>
                <p className="drag-text">
                  {dragActive
                    ? localization.drop_file_here || "Drop your file here"
                    : localization.drag_drop_file_here ||
                      "Drag and drop your file here"}
                </p>
                <p className="drag-or-text">or</p>
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="drag-browse-btn"
              >
                {localization.browse_files || "Browse Files"}
              </button>
              <p className="drag-supported-formats">
                {localization.supported_formats ||
                  "Supported formats: PDF, Documents, Spreadsheets"}
              </p>
            </div>
          </div>

          {files.length > 0 && (
            <div className="resource-upload-settings">
              <div className="resource-upload-setting">
                <span>{localization.use_for_rag || "Use for RAG"}</span>
                <Switch
                  checked={toRag}
                  onCheckedChange={setToRag}
                  disabled={isRagSwitchDisabled}
                />
              </div>
              <div className="resource-upload-setting">
                <span>{localization.public_resource || "Public Resource"}</span>
                <Switch
                  checked={visibilityScope === "public"}
                  onCheckedChange={(v) =>
                    !isVisibilityDisabled &&
                    setVisibilityScope(v ? "public" : "private")
                  }
                  disabled={isVisibilityDisabled}
                />
              </div>
            </div>
          )}

          {files.length > 0 && uploadError && (
            <p className="text-red-600 text-sm mb-2 mt-4 text-center">
              {uploadError}
            </p>
          )}

          {files.length > 0 && (
            <div className="resource-upload-preview">
              <h2 className="preview-title">
                {localization.selected_files || "Selected files"} (
                {files.length})
              </h2>
              <ul className="preview-list">
                {files.map((file, index) => (
                  <li key={index} className="file-card">
                    <div className="file-card-info">
                      <span className="file-icon">{getFileIcon(file)}</span>
                      <div>
                        <p className="file-name">{file.name}</p>
                        <p className="file-size">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setFiles([]);
                        setToRag(true);
                        setVisibilityScope("public");
                        if (fileInputRef.current)
                          fileInputRef.current.value = "";
                      }}
                      className="remove-resource-btn"
                    >
                      {localization.remove || "Remove"}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {files.length > 0 && (
            <div className="resource-upload-actions">
              <button
                onClick={() => {
                  setFiles([]);
                  setToRag(true);
                  setVisibilityScope("public");
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                className="resource-upload-cancel"
              >
                {localization.cancel || "Cancel"}
              </button>

              <button
                onClick={handleUpload}
                disabled={loading}
                className="resource-upload-submit"
              >
                {localization.upload || "Upload"} {files.length}{" "}
                {localization.file || "file"}
                {files.length > 1 ? "s" : ""}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
